import { get, isNil, sumBy } from 'lodash';
import { ImpactCategoryId } from '../../input/impacts/impact-categories';
import { applyValueSet, normalizeValueSet } from '../../input/value-sets';
import { ValueSet } from '../../input/value-sets/types';
import { Dimension, ORDERED_DIMENSIONS, ORDERED_IMPACTS_BY_DIMENSION, Valence, ValueChain } from '../../types';
import { NEGATIVE_INFINITE_NET_IMPACT_RATIO } from './compute-net-score';
import { legacyRelScoreToCentsPerDollar, legacyRelScoreToDollarsPerYear } from './monetary-intensity';
import type { ImpactIntervalsByTIVV, Leena2Interval } from './leena2-intervals';
import { FinalImpactScoreType, ImpactScoresByTIVV, NetScoreFormat, toImpactScoreIVKey } from './types';

/**
 * A helper utility that can be initialized with an impact score object, and provides reading functions for indidividual scores, dimension and valence sums,
 * while handling both monetary unit scaling (via set scoreType) and value set scaling (via set valueSet).
 *
 * Supports undefined scores and revenues since this is used to pass around in UI code like that. TODO: Instead separate into a no-undefined core class and a maybe-score-reader wrapper.
 */
export class ScoreReader {
  private rawScore: Partial<ImpactScoresByTIVV> | undefined;
  public readonly revenue: number | undefined;
  private scoreType: FinalImpactScoreType;
  private valueSet: ValueSet | undefined;

  private scoreTypeScaler: ScoreTypeScaler;
  private valueSetScaler: ValueSetScaler;

  public constructor(
    rawScore: Partial<ImpactScoresByTIVV> | undefined,
    scoreType: FinalImpactScoreType = FinalImpactScoreType.RELATIVE,
    revenue: number | undefined,
    valueSet?: ValueSet,
  ) {
    this.rawScore = rawScore;
    this.revenue = revenue;
    this.scoreType = scoreType;
    this.valueSet = valueSet;

    this.scoreTypeScaler = buildScoreTypeScaler(scoreType, revenue);
    this.valueSetScaler = buildValueSetScoreScaler(valueSet);
  }

  /**
   * Any argument with "undefined" tends to mean summing along that dimension.
   *
   * Example:
   * readScore(undefined, undefined, undefined, Valence.POSITIVE) would sum all positive impact in every impact category and value chain.
   *
   * Does not currently support summing impacts of single value chain (e.g. readScore(undefined, undefined, ValueChain.INTERNAL, undefined)) is forbidden.
   */
  public readScore = (
    impactCode: string | undefined,
    dimension: Dimension | undefined,
    valueChain: ValueChain | undefined,
    valence: Valence | undefined,
  ) => {
    if (!this.hasScore()) {
      return undefined; // Not initialized
    }
    if (valence === Valence.OTHER) {
      return undefined; // Not supported.
    }
    if (impactCode && valueChain && valence) {
      return this.readIVVWithScaling(impactCode as ImpactCategoryId, valueChain, valence);
    } else if (dimension && valueChain && valence) {
      return this.readDVVWithScaling(dimension, valueChain, valence);
    } else if (impactCode && !valueChain && valence) {
      return this.readIVWithScaling(impactCode as ImpactCategoryId, valence);
    } else if (dimension && !valueChain && valence) {
      return this.readDVWithScaling(dimension, valence);
    } else if (impactCode && !valueChain && !valence) {
      return this.readINetWithScaling(impactCode as ImpactCategoryId);
    } else if (dimension && !valueChain && !valence) {
      return this.readDNetWithScaling(dimension);
    } else if (!impactCode && !dimension && !valueChain && valence) {
      return this.readVWithScaling(valence);
    } else if (!impactCode && !dimension && !valueChain && !valence) {
      return this.readNetWithScaling();
    }
    throw Error('Operation not supported.');
  };

  /**
   * The 80% interval (Leena-2 Clef results, see `leena2-intervals.ts`) of the
   * category score `readScore(impactCode, undefined, undefined, valence)`
   * reads: from the same score type's intervals (absolute or relative), with
   * the same unit and value-set scaling. Every scaler is linear, so the bounds
   * scale exactly as the value does. Undefined when there is no score or no
   * interval for the category.
   */
  public readIntervalWithScaling(
    intervals: ImpactIntervalsByTIVV | null | undefined,
    impactCode: ImpactCategoryId,
    valence: Valence,
  ): Leena2Interval | undefined {
    if (!intervals || !this.hasScore() || valence === Valence.OTHER) {
      return undefined;
    }
    const key = toImpactScoreIVKey({ impactCode, valence });
    const byIV =
      this.scoreType === FinalImpactScoreType.ABSOLUTE
        ? intervals[FinalImpactScoreType.ABSOLUTE]
        : intervals[FinalImpactScoreType.RELATIVE];
    const interval = key ? byIV?.[key] : undefined;
    if (!interval) {
      return undefined;
    }
    const p10 = this.scaleWithCurrentScalers(interval.p10, impactCode, valence);
    const p90 = this.scaleWithCurrentScalers(interval.p90, impactCode, valence);
    if (isNil(p10) || isNil(p90)) {
      return undefined;
    }
    return { p10, p90 };
  }

  public clone(changes: {
    rawScore?: Partial<ImpactScoresByTIVV>;
    scoreType?: FinalImpactScoreType;
    revenue?: number | undefined;
    valueSet?: ValueSet | null;
  }) {
    return new ScoreReader(
      changes.rawScore ?? this.rawScore,
      changes.scoreType ?? this.scoreType,
      changes.revenue ?? this.revenue,
      changes.valueSet ?? (changes.valueSet === null ? undefined : this.valueSet),
    );
  }

  // Used to check that the score exists. It might not yet due to loading => no score readable
  private hasScore() {
    switch (this.scoreType) {
      case FinalImpactScoreType.RELATIVE:
      case FinalImpactScoreType.CENTS_PER_DOLLAR:
      case FinalImpactScoreType.ABSOLUTE:
        return !!this.scoreToReadFrom();
      case FinalImpactScoreType.DOLLARS_PER_YEAR:
        return !!this.scoreToReadFrom() && !!this.revenue;
    }
  }

  private scoreToReadFrom() {
    return this.scoreType === FinalImpactScoreType.ABSOLUTE
      ? this.rawScore?.[FinalImpactScoreType.ABSOLUTE]
      : this.rawScore?.[FinalImpactScoreType.RELATIVE];
  }

  private scaleWithCurrentScalers(unscaledScore: number, impactCode: ImpactCategoryId, valence: Valence) {
    const scoreTypeScaledScore = this.scoreTypeScaler(unscaledScore);
    if (isNil(scoreTypeScaledScore)) {
      return undefined;
    }
    return this.valueSetScaler(scoreTypeScaledScore, impactCode, valence);
  }

  private readIVV(impactCode: ImpactCategoryId, valueChain: ValueChain, valence: Valence) {
    const score = this.scoreToReadFrom();
    if (!score) {
      return undefined;
    }
    return get(score, [impactCode, valueChain, valence, 'value'], undefined);
  }

  private readIVVWithScaling(impactCode: ImpactCategoryId, valueChain: ValueChain, valence: Valence) {
    const unscaledScore = this.readIVV(impactCode, valueChain, valence);
    if (isNil(unscaledScore)) {
      return undefined;
    }
    return this.scaleWithCurrentScalers(unscaledScore, impactCode, valence);
  }

  private readDVVWithScaling(dimension: Dimension, valueChain: ValueChain, valence: Valence) {
    const impactCodes = ORDERED_IMPACTS_BY_DIMENSION[dimension];
    return sumBy(impactCodes, impactCode => this.readIVVWithScaling(impactCode, valueChain, valence) ?? 0);
  }

  private readIV(impactCode: ImpactCategoryId, valence: Valence) {
    return sumBy(VALUE_CHAINS, vc => this.readIVV(impactCode, vc, valence) ?? 0);
  }

  private readIVWithScaling(impactCode: ImpactCategoryId, valence: Valence) {
    const unscaledScore = this.readIV(impactCode, valence);
    return this.scaleWithCurrentScalers(unscaledScore, impactCode, valence);
  }

  private readINetWithScaling(impactCode: ImpactCategoryId) {
    const positive = this.readIVWithScaling(impactCode, Valence.POSITIVE);
    const negative = this.readIVWithScaling(impactCode, Valence.NEGATIVE);
    if (isNil(positive) || isNil(negative)) {
      return undefined;
    }
    return positive - negative;
  }

  private readDVWithScaling(dimension: Dimension, valence: Valence) {
    const impactCodes = ORDERED_IMPACTS_BY_DIMENSION[dimension];
    return sumBy(impactCodes, impactCode => this.readIVWithScaling(impactCode, valence) ?? 0);
  }

  private readDNetWithScaling(dimension: Dimension) {
    return this.readDVWithScaling(dimension, Valence.POSITIVE) - this.readDVWithScaling(dimension, Valence.NEGATIVE);
  }

  private readVWithScaling(valence: Valence) {
    return sumBy(ORDERED_DIMENSIONS.map(dimension => this.readDVWithScaling(dimension, valence)));
  }

  private readNetWithScaling() {
    return this.readVWithScaling(Valence.POSITIVE) - this.readVWithScaling(Valence.NEGATIVE);
  }
}

const VALUE_CHAINS = [ValueChain.ANY, ValueChain.DOWNSTREAM, ValueChain.INTERNAL, ValueChain.UPSTREAM] as const;

type ScoreTypeScaler = (score: number) => number | undefined;
export function buildScoreTypeScaler(
  impactScoreType: FinalImpactScoreType,
  revenue: number | undefined,
): ScoreTypeScaler {
  switch (impactScoreType) {
    case FinalImpactScoreType.RELATIVE:
    case FinalImpactScoreType.ABSOLUTE:
      return (score: number) => score;
    case FinalImpactScoreType.CENTS_PER_DOLLAR:
      return legacyRelScoreToCentsPerDollar;
    case FinalImpactScoreType.DOLLARS_PER_YEAR:
      return (score: number) => {
        if (!revenue) {
          return undefined;
        }
        return legacyRelScoreToDollarsPerYear(score, revenue);
      };
  }
}

type ValueSetScaler = (score: number, impactCode: ImpactCategoryId, valence: Valence) => number;
export function buildValueSetScoreScaler(valueSet: ValueSet | undefined): ValueSetScaler {
  if (!valueSet) {
    return (score: number) => score;
  }
  const normalizedValueSet = normalizeValueSet(valueSet);
  return (score: number, impactCode: ImpactCategoryId, valence: Valence) => {
    return applyValueSet(normalizedValueSet, score, impactCode, valence);
  };
}

function getScoreReaderRatio(scoreReader: ScoreReader) {
  const positive = scoreReader.readScore(undefined, undefined, undefined, Valence.POSITIVE);
  const negative = scoreReader.readScore(undefined, undefined, undefined, Valence.NEGATIVE);
  if (isNil(positive) || isNil(negative)) {
    return undefined;
  }
  if (positive - negative === 0) {
    return 0;
  }
  return positive ? (positive - negative) / positive : NEGATIVE_INFINITE_NET_IMPACT_RATIO; // special case -Infinity
}

function getScoreReaderSum(scoreReader: ScoreReader) {
  return scoreReader.readScore(undefined, undefined, undefined, undefined);
}

export function getScoreReaderTotal(scoreReader: ScoreReader, netScoreFormat: NetScoreFormat) {
  switch (netScoreFormat) {
    case NetScoreFormat.NET_RATIO:
    case NetScoreFormat.NET_RATIO_DECIMAL:
      return getScoreReaderRatio(scoreReader);
    case NetScoreFormat.NET_SUM:
      return getScoreReaderSum(scoreReader);
  }
}

export function getScoreReaderValenceSums(scoreReader: ScoreReader) {
  const pos = scoreReader.readScore(undefined, undefined, undefined, Valence.POSITIVE);
  const neg = scoreReader.readScore(undefined, undefined, undefined, Valence.NEGATIVE);
  const net = pos && neg ? pos - neg : undefined;
  return { pos, neg, net };
}
